import React, { useState, useEffect, useRef } from "react";
import CommonModal from "../../../../../Modals/CommonModal";
import Input from "../../../../../Elements/Input";
import Button from "../../../../../Elements/Button";
import { networkService } from "../../../../../config/axios";
import { ToasterNotification } from "../../../../../CommonComponents/Toasts/ToastNotification";
import { Toast, ToastContainer } from 'react-bootstrap';
import "../../../../../CommonComponents/Toasts/ToastNotification.css";
import {
  getCookie,
  setCookie,
  setReceiverCookie,
  setReceiverSession,
} from "../../../../../../utis/helper";
import { checkPageVisibility } from "../../../../../../hooks/utils";

const SettingsEnableOPTIN = ({ tlb, sess_id, actionCableOptIn, onModalOpenChange }) => {
  const showImmediatelyRef = useRef(
    sessionStorage.getItem(`optin_refresh_${tlb?.token}`) === 'true'
  );
  const [isModalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [telephoneNumber, setTelephoneNumber] = useState("");
  const [existingReceiverId, setExistingReceiverId] = useState(null);
  const [modalBodyText, setModalBodyText] = useState(null);
  const [alertNotification, setAlertNotification] = useState(null);
  const [pageNumber, setPageNumber] = useState(tlb?.landing_page || 1);
  const optinCookie = getCookie("optin", {});
  const [isApiCalled, setIsApiCalled] = useState(
    sessionStorage.getItem(`optin_completed_${tlb?.token}`) === 'true'
  );

  const [emailError, setEmailError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [telephoneNumberError, setTelephoneNumberError] = useState("");
  const [showCustomToast, setShowCustomToast] = useState(false);
  const [customToastMessage, setCustomToastMessage] = useState("");
  const [customToastVariant, setCustomToastVariant] = useState("success");

  const showOptInModal = () => {
    optinCookie[tlb?.token] = {
      show: "display",
    };
    setCookie("optin", optinCookie);
    if (!isModalOpen) {
      setModalOpen(true);
      onModalOpenChange?.(true);
    }
  };

  const closeOptInModal = () => {
    setModalOpen(false);
    onModalOpenChange?.(false);
  };

  const onScroll = (e) => {
    const value = parseInt(e.target.value);
    if (window.__kdf_orientation_changing) {
      return;
    }
    setPageNumber(value);
  };

  // Mount effect: attach listener, clear refresh flag, sync page from input
  useEffect(() => {
    const input = document.getElementById("pageNumber");
    if (!input) return;
    input.addEventListener("change", onScroll);
    if (showImmediatelyRef.current) {
      sessionStorage.removeItem(`optin_refresh_${tlb?.token}`);
    }
    const currentPage = parseInt(input.value, 10);
    if (currentPage > 0 && currentPage !== pageNumber) {
      setPageNumber(currentPage);
    }
    return () => input.removeEventListener("change", onScroll);
  }, []);

  useEffect(() => {
    if (window.__kdf_orientation_changing) return;
    if (isApiCalled) return;

    if (tlb?.has_optin !== true) return;

    const isOnOptInPage =
      (tlb?.optin_2nd_page_viewed &&
        (tlb?.landing_page > 1 ? (pageNumber === tlb?.landing_page + 1 ||
          pageNumber === tlb?.landing_page - 1) : pageNumber != 1)) ||
        (!tlb?.optin_2nd_page_viewed &&
          pageNumber === tlb?.opt_in.show_on_page);

    if (!isOnOptInPage) return;

    // Show immediately on refresh (no delay)
    if (showImmediatelyRef.current) {
      showImmediatelyRef.current = false;
      showOptInModal();
      actionCableOptIn?.({ status: "shown", source: "modal" });
      return;
    }

    let showOptinIn = tlb.opt_in.show_delay;

    // Handle 0-second delay
    if (showOptinIn === 0) {
      if (checkPageVisibility()) {
        showOptInModal();
        actionCableOptIn?.({ status: "shown", source: "modal" });
      }
      return;
    }

    const showOptinInterval = setInterval(function () {
      if (checkPageVisibility()) {
        if (1 == showOptinIn) {
          showOptInModal();
          actionCableOptIn?.({ status: "shown", source: "modal" });
          clearInterval(showOptinInterval);
        } else {
          showOptinIn--;
        }
      }
    }, 1000);

    return () => clearInterval(showOptinInterval);
  }, [pageNumber, tlb?.has_optin, isApiCalled]);

  const handleReceiver = async (url, payload, method) => {
    const { data } = await networkService(
      url,
      method,
      payload
    );

    return data;
  }

  const handleNext = async () => {
    let valid = true;
    setEmailError("");
    setFirstNameError("");
    setLastNameError("");
    setTelephoneNumberError("");

    if (step === 1) {
      if (!email) {
        setEmailError("Email is required");
        valid = false;
      }
    } else if (step === 2) {
      if (tlb?.opt_in?.ask_name) {
        if (!firstName || firstName.trim().length < 2) {
          setFirstNameError("First Name must be between 2 and 20 characters");
          valid = false;
        }
        if (!lastName || lastName.trim().length < 2) {
          setLastNameError("Last Name must be between 2 and 20 characters");
          valid = false;
        }
      }
      if (tlb?.opt_in?.ask_telephone_number) {
        if (!telephoneNumber) {
          setTelephoneNumberError("Please Provide a valid phone number");
          valid = false;
        }
      }
    }

    if (!valid) {
      return;
    }
    try {
      if (step === 1) {
        let { data } = await networkService(
          "check_receiver/demo",
          "POST",
          { company_id: tlb?.tracking_link_company?.id ?? null, email: email }
        );
        if (data?.receiver == null) {
          setStep(2);
        } else {
          const receiver = data.receiver;
          const needsName = tlb?.opt_in?.ask_name && !receiver.first_name;
          const needsPhone = tlb?.opt_in?.ask_telephone_number && !receiver.telephone_number;

          if (needsName || needsPhone) {
            setExistingReceiverId(receiver.id);
            if (receiver.first_name) setFirstName(receiver.first_name);
            const subsequentText = tlb?.opt_in?.custom_subsequent_text;
            if (subsequentText) {
              const personalised = receiver.first_name
                ? receiver.first_name + subsequentText.replace("[FIRST NAME],", "")
                : subsequentText.replace("[FIRST NAME],", "");
              setModalBodyText(personalised);
            }
            setStep(2);
          } else {
            const receiverFirstName = receiver.first_name || "";
            const displayName = receiverFirstName
              ? receiverFirstName.charAt(0).toUpperCase() + receiverFirstName.slice(1)
              : email;
            const successMessage = `Thanks for confirming ${displayName}`;
            await handleReceiver(`/receivers/${receiver.id}.json`, {
              email: email,
              session_id: sess_id,
              tracking_link_id: tlb?.opt_in?.tracking_link_id,
            }, 'PATCH');
            setReceiverCookie(tlb?.token, { id: receiver.id });
            await setReceiverSession(receiver.id, tlb?.id, window?.objInit?.sess_id);
            actionCableOptIn?.({ status: "completed", source: "existing" });
            sessionStorage.setItem(`optin_completed_${tlb?.token}`, 'true');
            setIsApiCalled(true);
            optinCookie[tlb?.token] = { show: "none" };
            setCookie("optin", optinCookie);
            setCustomToastMessage(successMessage);
            setCustomToastVariant("success");
            setShowCustomToast(true);
            closeOptInModal();
          }
        }
      } else if (step === 2) {
        const payload = {
          email: email,
          first_name: firstName,
          last_name: lastName,
          session_id: sess_id,
          tracking_link_id: tlb?.opt_in?.tracking_link_id,
          telephone_number: telephoneNumber,
        };
        const data = existingReceiverId
          ? await handleReceiver(`/receivers/${existingReceiverId}.json`, payload, 'PATCH')
          : await handleReceiver("receivers/data/add.json", payload, 'POST');
        setReceiverCookie(tlb?.token, { id: data.id });
        await setReceiverSession(data.id, tlb?.id, window?.objInit?.sess_id);
        actionCableOptIn?.({ status: "completed", source: "new" });
        sessionStorage.setItem(`optin_completed_${tlb?.token}`, 'true');
        setIsApiCalled(true);
        const receiverFirstName = data?.first_name || firstName || "";
        let successMessage;
        if (existingReceiverId) {
          const displayName = receiverFirstName
            ? receiverFirstName.charAt(0).toUpperCase() + receiverFirstName.slice(1)
            : email;
          successMessage = `Thanks for confirming ${displayName}`;
        } else {
          successMessage = receiverFirstName !== ""
            ? `Thanks ${receiverFirstName}, we emailed you a link to this doc for future access. We also unlocked the doc so that you can continue viewing now.`
            : `Thanks for confirming ${email}`;
        }
        setCustomToastMessage(successMessage);
        setCustomToastVariant("success");
        setShowCustomToast(true);
        closeOptInModal();
        optinCookie[tlb?.token] = {
          show: "none",
        };
        setCookie("optin", optinCookie);
      }
    } catch (error) {
      setAlertNotification({
        message: "Email has already been taken",
        variant: "danger",
      });
    }
  };

  return (
    <>
      {alertNotification != null && !showCustomToast && (
        <ToasterNotification
          message={alertNotification?.message}
          variant={alertNotification?.variant}
        />
      )}
      {showCustomToast && (
        <ToastContainer position="top-end">
          <Toast
            show={showCustomToast}
            onClose={() => setShowCustomToast(false)}
            delay={8000}
            autohide
            className={`${customToastVariant === 'success' ? 'toast-custom-green' :
                        customToastVariant === 'danger' ? 'toast-custom-red' :
                        customToastVariant === 'alert' ? 'toast-alert' :
                        customToastVariant === 'secondary' && 'toast-custom-blue'} text-white toast-notify`}
          >
            <Toast.Body>{customToastMessage}</Toast.Body>
          </Toast>
        </ToastContainer>
      )}
      <CommonModal
        title="Your permission is needed"
        modalStatus={isModalOpen}
        changeActiveStatus={() => { }}
        centered
        classList="hideCross optSettingsModal"
        closeModalOnEsc={false}
        footer={
          <div
            className={`d-flex ${step === 1
              ? tlb?.opt_in?.ask_name || tlb?.opt_in?.ask_telephone_number
                ? "text-end"
                : tlb?.opt_in?.custom_btn_text ?? "text-start"
              : tlb?.opt_in?.custom_btn_text ?? "text-start"
              }`}
          >
            <Button
              text={
                step === 1
                  ? tlb?.opt_in?.ask_name || tlb?.opt_in?.ask_telephone_number
                    ? "Next"
                    : tlb?.opt_in?.custom_btn_text ?? "OK"
                  : tlb?.opt_in?.custom_btn_text ?? "OK"
              }
              classList="success"
              addonClass="my-2 mx-2 common-modal-yes-button"
              onClick={handleNext}
            />
          </div>
        }
      >
        <div className="">
          <div>
            <p
              dangerouslySetInnerHTML={{
                __html:
                  modalBodyText ??
                  tlb?.opt_in?.custom_text ??
                  "To continue reading, please provide your:",
              }}
            />
          </div>
          {step === 1 && (
            <>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
                className="fs-6"
                autoFocus
              />
              {emailError && <div className="error-message">{emailError}</div>}
            </>
          )}

          {(tlb?.opt_in?.ask_name || tlb?.opt_in?.ask_telephone_number) &&
            step === 2 && (
              <>
                {tlb?.opt_in?.ask_name && (
                  <div className="form-group d-flex gap-3">
                    <div className="form-group-item flex-fill">
                    <Input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First Name"
                      required
                      minLength={2}
                      maxLength={20}
                      className="fs-6"
                      autoFocus
                    />
                    {firstNameError && (
                      <div className="error-message">{firstNameError}</div>
                    )}
                    </div>

                    <div className="form-group-item flex-fill">
                    <Input
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last Name"
                      required
                      minLength={2}
                      maxLength={20}
                      className="fs-6"
                    />
                    {lastNameError && (
                      <div className="error-message">{lastNameError}</div>
                    )}
                    </div>
                  </div>
                )}
                {tlb?.opt_in?.ask_telephone_number && (
                  <>
                    <Input
                      type={"number"}
                      onKeyDown={(evt) =>
                        ["e", "E", "+", "-"].includes(evt.key) &&
                        evt.preventDefault()
                      }
                      classList={"full-width"}
                      value={telephoneNumber}
                      onChange={(e) => setTelephoneNumber(e.target.value)}
                      placeholder="Tel nr."
                      required
                    />
                    {telephoneNumberError && (
                      <div className="error-message">
                        {telephoneNumberError}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

          <div className="permission-paragraph mt-2 mb-2">
            <p>
              You consent to us processing your personal data. Unsubscribe
              anytime. See our{" "}
              <span className="privacy">
                <a href={window?.objInit?.tlb?.company_privay_url || window?.objInit?.profile?.privacy_link || "https://kinetizine.com/privacy"} target="_blank" rel="noopener noreferrer" tabIndex={-1}>Privacy Notice</a>
              </span>
            </p>
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default SettingsEnableOPTIN;
